import type { FC } from "react";
import { useEffect } from "react";

import dompurify from "isomorphic-dompurify";

import { Typography } from "@jti/ui/components/Typography";

import type { TierProps } from "./Tier";
import { Tier } from "./Tier";
import { TierStatus } from "./TierStatus";

import styles from "./TierBenefits.module.scss";

const { sanitize } = dompurify;

export type TierStatusType = "yes" | "no" | "other";

export type TierGeneralType = {
  name: string;
  description: string;
};

export type TierBenefitsSection = {
  name: string;
  tier1State: TierStatusType;
  tier1Label: string;
  tier2State: TierStatusType;
  tier2Label: string;
  tier3State: TierStatusType;
  tier3Label: string;
  isHighLightened?: boolean;
};

export type TierBenefitsSections = {
  name: string;
  benefits: TierBenefitsSection[];
};

export type TiersBenefitsProps = {
  general: TierGeneralType;
  tier1: TierProps;
  tier2: TierProps;
  tier3: TierProps;
  benefitsSections: TierBenefitsSections[];
  componentId?: string;
};

export const ACTIVE_CLASS = "active";
export const TIER_DATA_TEST_ID = "tierBenefitsItem";

export const TiersBenefits: FC<TiersBenefitsProps> = ({
  general,
  tier1,
  tier2,
  tier3,
  benefitsSections = [],
  componentId,
}) => {
  const handleMobileTierChange = (entries: IntersectionObserverEntry[], tiersSection: Element | null) => {
    if (!tiersSection) return;

    const mostVisibleEntry = entries.reduce<IntersectionObserverEntry | null>(
      (current, entry) =>
        !current || (entry.intersectionRatio ?? 0) > (current.intersectionRatio ?? 0) ? entry : current,
      null
    );
    if (!mostVisibleEntry) return;

    const parent = mostVisibleEntry.target.parentElement;
    parent
      ?.querySelectorAll<HTMLElement>(`.${styles.tierItem}.${ACTIVE_CLASS}`)
      .forEach((el) => el.classList.remove(ACTIVE_CLASS));

    mostVisibleEntry.target.classList.add(ACTIVE_CLASS);

    const tierId = mostVisibleEntry.target.getAttribute("data-tierid");
    if (!tierId) return;

    tiersSection
      .querySelectorAll<HTMLElement>(`[data-tierid].${ACTIVE_CLASS}`)
      .forEach((el) => el.classList.remove(ACTIVE_CLASS));

    tiersSection
      .querySelectorAll<HTMLElement>(`[data-tierid='${tierId}']`)
      .forEach((el) => el.classList.add(ACTIVE_CLASS));
  };

  useEffect(() => {
    if (!componentId) return;
    const tierBenefits = document.getElementById(componentId);
    if (!tierBenefits) return;
    const tiers = Array.from(tierBenefits.querySelectorAll(`[data-testid=${TIER_DATA_TEST_ID}]`));
    const tiersSection = tierBenefits.querySelector(`.${styles.sectionWrapper}`);

    const options = {
      root: tierBenefits.querySelector(`.${styles.tiersSection}`),
      threshold: 0.9,
    };

    const observer = new IntersectionObserver((entries) => handleMobileTierChange(entries, tiersSection), options);
    tiers.forEach((tier) => observer.observe(tier));
    // Should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { name, description } = general;

  return (
    <div className={styles.tierBenefits} id={componentId}>
      <div className={styles.topContent}>
        <div className={styles.textWrapper}>
          <Typography variant="h3">{name}</Typography>
          {!!description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitize(description) }} />
          )}
        </div>
        <div className={styles.tiersSection}>
          {[tier1, tier2, tier3].map(({ name, description, asset, altText }, index) => (
            <Tier
              dataTestId={TIER_DATA_TEST_ID}
              name={name}
              description={description}
              asset={asset}
              altText={altText}
              id={index}
              key={name}
            />
          ))}
        </div>
      </div>
      <div className={styles.table}>
        <div className={styles.sectionWrapper}>
          {benefitsSections.map(({ name, benefits }) => (
            <div className={styles.section} key={name} data-testid="tierBenefitsSection">
              <div className={styles.title}>
                <div className={styles.titleContent}>{name}</div>
              </div>
              {benefits.map(
                ({ name, tier1Label, tier1State, tier2Label, tier2State, tier3Label, tier3State, isHighLightened }) => {
                  return (
                    <div className={styles.sectionItem} key={name} data-testid="tierBenefitsItem">
                      <span className={styles.sectionItemName}>{name}</span>
                      <div className={styles.sectionItemStatus} data-tierid={0}>
                        <TierStatus isHighLightened={isHighLightened} status={tier1State} label={tier1Label} />
                      </div>
                      <div className={styles.sectionItemStatus} data-tierid={1}>
                        <TierStatus isHighLightened={isHighLightened} status={tier2State} label={tier2Label} />
                      </div>
                      <div className={styles.sectionItemStatus} data-tierid={2}>
                        <TierStatus isHighLightened={isHighLightened} status={tier3State} label={tier3Label} />
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
